import * as React from 'react'
import { FC, useMemo, useState } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { mergeTerritories } from '@user/store/territory/api'
import { Territory } from '@user/store/territory/typings'
import { DEFAULT_TERRITORY_COLOR } from '@user/store/territory/constants'

type Props = {
  /** The territories picked in the list. Always two or more. */
  selected: Territory[]
  onCancel: () => void
  onMerged: () => void
}

/**
 * Combine the selected territories into one (WWM-17508).
 *
 * The survivor is chosen explicitly rather than defaulting to whichever was ticked first,
 * because it decides which name, colour and id live on -- and a merge cannot be undone by
 * un-ticking a box.
 *
 * Territories that do not share a border are refused by the server. That is not a limitation
 * worth hiding: a territory is stored as a single polygon, so two separate areas have nowhere
 * to go, and the message says to merge ones that touch.
 */
export const TerritoryMergeDialog: FC<Props> = ({
  selected,
  onCancel,
  onMerged,
}) => {
  const dispatch = useAppDispatch()

  // Largest first: the biggest area is usually the one whose name should survive.
  const ordered = useMemo(
    () =>
      [...selected].sort(
        (a, b) =>
          (b.bounds.maxLatitude - b.bounds.minLatitude) *
            (b.bounds.maxLongitude - b.bounds.minLongitude) -
          (a.bounds.maxLatitude - a.bounds.minLatitude) *
            (a.bounds.maxLongitude - a.bounds.minLongitude),
      ),
    [selected],
  )

  const [targetId, setTargetId] = useState<number>(ordered[0].id)
  const [merging, setMerging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const target = ordered.find((t) => t.id === targetId)
  const absorbed = ordered.filter((t) => t.id !== targetId)

  const confirm = async () => {
    setMerging(true)
    setError(null)

    const result = await dispatch(
      mergeTerritories({
        targetId,
        sourceIds: absorbed.map((t) => t.id),
      }),
    )

    setMerging(false)

    if (mergeTerritories.rejected.match(result)) {
      setError(result.payload?.message ?? 'Could not merge these territories.')
      return
    }

    onMerged()
  }

  return (
    <Dialog
      open
      onClose={merging ? undefined : onCancel}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Merge {ordered.length} territories</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          select
          size="small"
          fullWidth
          label="Keep which territory?"
          value={targetId}
          onChange={(e) => setTargetId(Number(e.target.value))}
          helperText="This one keeps its name and colour; the others are merged into it."
          data-test-id="territory-merge-target"
        >
          {ordered.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '2px',
                  mr: 1,
                  bgcolor: t.colorHex ?? DEFAULT_TERRITORY_COLOR,
                }}
              />
              {t.name}
            </MenuItem>
          ))}
        </TextField>

        <DialogContentText sx={{ mt: 2 }}>
          {/* Names what is lost, not just what happens. Owners are kept deliberately --
              dropping one would quietly take an area off a rep. */}
          &ldquo;{target?.name}&rdquo; will take in{' '}
          {absorbed.map((t) => `“${t.name}”`).join(' and ')}, and their owners
          come with them. The merged-in territories are archived.
        </DialogContentText>

        <DialogContentText
          sx={{ mt: 1.5, fontSize: 13, color: 'text.secondary' }}
        >
          They have to share a border. Territories that do not touch cannot be
          combined into one shape.
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={merging}>
          Cancel
        </Button>
        <Button
          onClick={confirm}
          variant="contained"
          disabled={merging}
          data-test-id="territory-merge-confirm"
        >
          {merging ? 'Merging…' : 'Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
